<template>
  <transition name="slide-in">
    <div
      v-if="open"
      ref="panelRef"
      class="info-panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <div class="info-panel__header">
        <h2 :id="titleId" class="info-panel__title">{{ title }}</h2>
        <button
          class="info-panel__close"
          :aria-label="t('player.controls.closePanel')"
          @click="emit('close')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
          </svg>
        </button>
      </div>
      <div class="info-panel__content">
        <slot />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, type Ref } from 'vue'
import { useVideoI18n } from '../../i18n'
import { useFocusManagement } from '../../composables/useFocusManagement'

/**
 * Side panel chrome (header + close button + slide-in) shared by the video
 * player's `InfoPanel` and the collection shell's playlist drawer, so both
 * render the exact same drawer.
 */
interface Props {
  open: boolean
  title: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useVideoI18n()

const panelRef = ref<HTMLElement | null>(null)
const isOpen = computed(() => props.open)

useFocusManagement(panelRef as Ref<HTMLElement | null>, isOpen)

const titleId = `player-panel-title-${Math.random().toString(36).slice(2, 9)}`
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.info-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: 320px;
  max-width: 85%;
  height: 100%;
  background-color: rgba(18, 18, 18, 0.82);
  backdrop-filter: blur(20px);
  z-index: 80;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }

  // A narrow player is a phone's width, and a strip of video beside the
  // panel is too thin to watch: the panel takes the whole frame, opaque so
  // nothing shows through, under a header that leaves the content the height.
  @include player-compact {
    width: 100%;
    max-width: 100%;
    background-color: rgba(18, 18, 18, 0.96);

    &__header {
      padding: 2px 2px 2px 12px;
    }

    &__title {
      font-size: 0.8125rem;
    }
  }
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.25s ease;
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .slide-in-enter-active,
  .slide-in-leave-active {
    transition-duration: 1ms;
  }
}

// Light theme
[data-vp-theme='light'] .info-panel {
  background-color: #fafbfc;

  &__header {
    border-bottom-color: rgba(0, 0, 0, 0.1);
  }

  &__title {
    color: rgba(0, 0, 0, 0.9);
  }

  &__close {
    color: rgba(0, 0, 0, 0.6);

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.9);
    }
  }

  &__content {
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }
}
</style>
